import React, { useState, useMemo, useEffect } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "../../appointmentsPage.module.css";
import { IAppointment, IMonthDay } from "../../types/appointments.types";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import AppointmentDetailsModal from "../AppointmentDetailsModal/AppointmentDetailsModal";
import {
  getAppointmentColors,
  truncateText,
} from "../../utils/fundingSourceColors";
import {
  getInitialDateForView,
  saveDateForView,
} from "../../utils/calendarStorage";
import CreateAppointmentModal from "../CreateAppointmentModal/CreateAppointmentModal";
import ExportTableButtons from "../../../../components/exportTableButtons/ExportTableButtons";
import {
  buildAppointmentsExport,
  buildSearchFilterSummary,
} from "../../utils/buildAppointmentsExport";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

/**
 * Monthly View Component - Client Version (Read-Only)
 *
 * Displays appointments in a monthly calendar grid.
 * Simplified read-only version without drag-drop or editing.
 */
const MonthlyView: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(() =>
    getInitialDateForView("monthly"),
  );
  const [selectedAppointment, setSelectedAppointment] =
    useState<IAppointment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Create Appointment State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDate, setCreateModalDate] = useState("");
  const [createModalTime, setCreateModalTime] = useState("09:00");

  // Day Modal State (for when there are many appointments in a day)
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<IMonthDay | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Use appointments data hook
  const { searchedAppointments, searchTerm } = useAppointmentsContext();

  // Persist date when it changes (for navigation back)
  useEffect(() => {
    saveDateForView("monthly", currentDate);
  }, [currentDate]);

  // Filter appointments for the month locally from searched appointments
  const monthlyAppointments = useMemo(() => {
    return searchedAppointments;
  }, [searchedAppointments]);

  // Export payload (PDF/Excel) for the currently displayed month (filtered to this month).
  const exportProps = useMemo(() => {
    const monthRows = monthlyAppointments.filter((a) => {
      const d = new Date(a.date);
      return d.getFullYear() === year && d.getMonth() === month;
    });
    return buildAppointmentsExport(monthRows, {
      viewType: "monthly",
      rangeLabel: currentDate.toLocaleDateString(getLocale(), {
        month: "long",
        year: "numeric",
      }),
      filterSummary: buildSearchFilterSummary(searchTerm),
    });
  }, [monthlyAppointments, year, month, currentDate, searchTerm]);

  const dayNames = [
    t("SUNDAY"),
    t("MONDAY"),
    t("TUESDAY"),
    t("WEDNESDAY"),
    t("THURSDAY"),
    t("FRIDAY"),
    t("SATURDAY"),
  ];

  // Helper function to format date as YYYY-MM-DD
  const formatDateString = (
    year: number,
    month: number,
    day: number,
  ): string => {
    const paddedMonth = String(month + 1).padStart(2, "0");
    const paddedDay = String(day).padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
  };

  // Generate calendar days
  const calendarDays: IMonthDay[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = formatDateString(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay(); // 0 (Sunday) to 6 (Saturday)
    const totalDays = lastDay.getDate();

    const days: IMonthDay[] = [];

    // Previous month days (padding)
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const dayNum = prevMonthLastDay - i;
      const dateStr = formatDateString(year, month - 1, dayNum);
      const date = new Date(year, month - 1, dayNum);
      days.push({
        date: dateStr,
        dayNumber: dayNum,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 5 || date.getDay() === 6,
        appointments: [],
        totalCount: 0,
      });
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = formatDateString(year, month, day);
      const date = new Date(year, month, day);
      const dayAppointments = monthlyAppointments.filter(
        (apt) => apt.date === dateStr,
      );

      days.push({
        date: dateStr,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        isWeekend: date.getDay() === 5 || date.getDay() === 6,
        appointments: dayAppointments.slice(0, 3),
        totalCount: dayAppointments.length,
      });
    }

    // Next month days to fill grid (6 rows x 7 cols = 42)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const dateStr = formatDateString(year, month + 1, i);
      const date = new Date(year, month + 1, i);
      days.push({
        date: dateStr,
        dayNumber: i,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 5 || date.getDay() === 6,
        appointments: [],
        totalCount: 0,
      });
    }

    return days;
  }, [year, month, monthlyAppointments]);

  // Calculate total appointments for current month only
  const currentMonthTotal = useMemo(() => {
    return calendarDays.reduce(
      (sum, day) => (day.isCurrentMonth ? sum + day.totalCount : sum),
      0,
    );
  }, [calendarDays]);

  const handleAppointmentClick = (
    e: React.MouseEvent,
    appointment: IAppointment,
  ) => {
    e.stopPropagation();
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleMoreAppointmentsClick = (e: React.MouseEvent, day: IMonthDay) => {
    e.stopPropagation();
    // Re-fetch all appointments for the day
    const allApts = monthlyAppointments.filter((apt) => apt.date === day.date);
    setSelectedDay({ ...day, appointments: allApts });
    setIsDayModalOpen(true);
  };

  const handleMonthChange = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1));
    setCurrentDate(newDate);
  };

  const getDayClassName = (day: IMonthDay): string => {
    let className = styles.calendarDay;

    if (!day.isCurrentMonth) className += " " + styles.calendarDayOtherMonth;
    if (day.isToday) className += " " + styles.calendarDayToday;
    if (day.isWeekend) className += " " + styles.calendarDayWeekend;

    return className;
  };

  return (
    <div>
      {/* Statistics */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{currentMonthTotal}</div>
          <div className={styles.statLabel}>
            {t("APPOINTMENTS_TITLE") || 'סה"כ פגישות'}
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <h2 className={styles.calendarTitle} style={{ margin: 0 }}>
            {currentDate.toLocaleDateString(getLocale(), {
              month: "long",
              year: "numeric",
            })}
          </h2>
          <div className={styles.calendarNav}>
            <button
              className={styles.navBtn}
              onClick={() => handleMonthChange("next")}
              aria-label="Next"
            >
              ‹
            </button>
            <button
              className={styles.todayBtn}
              onClick={() => setCurrentDate(new Date())}
            >
              {t("APPOINTMENTS_TODAY")}
            </button>
            <span className={styles.currentDate}>
              {currentDate.toLocaleDateString(getLocale(), {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              className={styles.navBtn}
              onClick={() => handleMonthChange("prev")}
              aria-label="Previous"
            >
              ›
            </button>
            <ExportTableButtons
              disabled={exportProps.dataRows.length === 0}
              {...exportProps}
            />
          </div>
        </div>

        {/* Monthly Calendar Grid */}
        <div className={styles.scrollableContainer}>
          <div className={styles.monthlyCalendar}>
            {/* Weekday Headers */}
            {dayNames.map((dayName) => (
              <div key={dayName} className={styles.weekdayHeader}>
                {dayName}
              </div>
            ))}

            {/* Calendar Days */}
            {calendarDays.map((day, index) => (
              <div
                key={`${day.date}-${index}`}
                className={getDayClassName(day)}
              >
                <div
                  className={`${styles.dayNumber} ${
                    day.isToday ? styles.dayNumberToday : ""
                  }`}
                >
                  {day.dayNumber}
                </div>

                <button
                  className={styles.addAppointmentBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreateModalDate(day.date);
                    setCreateModalTime("09:00");
                    setShowCreateModal(true);
                  }}
                >
                  +
                </button>

                {day.isCurrentMonth && (
                  <>
                    <div className={styles.dayAppointments}>
                      {day.appointments.map((apt) => {
                        const fundingColor = getAppointmentColors(apt);
                        const isGoogleEvent = apt.source === "google_calendar";
                        const isOther =
                          apt.source === "other" ||
                          (!apt.therapistsId &&
                            !apt.therapistId &&
                            !apt.treatmentGroupName &&
                            !apt.mediaId &&
                            !isGoogleEvent);
                        return (
                          <div
                            key={apt.id}
                            className={styles.appointmentIndicator}
                            style={{
                              borderRightColor: fundingColor.borderColor,
                              backgroundColor: fundingColor.backgroundColor,
                              cursor: "pointer",
                            }}
                            onClick={(e) => handleAppointmentClick(e, apt)}
                          >
                            <span>
                              {isGoogleEvent ? "📅 " : isOther ? "📝 " : ""}
                              {apt.time}
                            </span>{" "}
                            <span
                              style={
                                !apt.patientName && apt.treatmentGroupName
                                  ? { color: "#10B981", fontWeight: 500 }
                                  : undefined
                              }
                            >
                              {isOther
                                ? truncateText(
                                    apt.comments || t("OTHER") || "אחר",
                                  )
                                : !apt.patientName && apt.treatmentGroupName
                                  ? truncateText(apt.treatmentGroupName)
                                  : truncateText(apt.therapistName)}
                            </span>
                          </div>
                        );
                      })}

                      {day.totalCount > 3 && (
                        <div
                          className={styles.moreAppointments}
                          onClick={(e) => handleMoreAppointmentsClick(e, day)}
                        >
                          +{day.totalCount - 3} {t("APPOINTMENTS_MORE")}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Appointment Details Modal */}
      {isModalOpen && selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
        />
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <CreateAppointmentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            // Handle success / refresh
          }}
          initialDate={createModalDate}
          initialTime={createModalTime}
        />
      )}

      {/* Day Appointments Modal */}
      {isDayModalOpen && selectedDay && (
        <div
          className={styles.modalOverlay}
          onClick={(e) =>
            e.target === e.currentTarget && setIsDayModalOpen(false)
          }
        >
          <div className={styles.timeSlotModal}>
            <div className={styles.modalHeader}>
              <h3>
                {new Date(selectedDay.date).toLocaleDateString(getLocale(), {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsDayModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {selectedDay.appointments
                .sort((a, b) => (a.time || "").localeCompare(b.time || ""))
                .map((apt) => {
                  const fundingColor = getAppointmentColors(apt);
                  const isGoogleEvent = apt.source === "google_calendar";
                  const isOther =
                    apt.source === "other" ||
                    (!apt.therapistsId &&
                      !apt.therapistId &&
                      !apt.treatmentGroupName &&
                      !apt.mediaId &&
                      !isGoogleEvent);
                  return (
                    <div
                      key={apt.id}
                      className={styles.modalAppointmentItem}
                      style={{
                        borderRight: `4px solid ${fundingColor.borderColor}`,
                      }}
                      onClick={() => {
                        setIsDayModalOpen(false);
                        setSelectedAppointment(apt);
                        setIsModalOpen(true);
                      }}
                    >
                      <div className={styles.modalAppointmentTime}>
                        {apt.time}
                      </div>
                      <div className={styles.modalAppointmentDetails}>
                        <div className={styles.modalAppointmentPatient}>
                          {isGoogleEvent ? "📅 " : isOther ? "📝 " : ""}
                          {isOther
                            ? truncateText(apt.comments || t("OTHER") || "אחר")
                            : apt.treatmentGroupName ||
                              truncateText(apt.patientName)}
                        </div>
                        <div className={styles.modalAppointmentSub}>
                          {isOther ? (
                            <>
                              {t("APPOINTMENTS_NOTES")}:{" "}
                              {truncateText(apt.comments)}
                            </>
                          ) : (
                            <>
                              {t("THERAPIST_NAME")}:{" "}
                              {truncateText(apt.therapistName)}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyView;
